import { PERMISSIONS, POLICY_STATUS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreateCancellationPolicy, useUpdateCancellationPolicy } from '../api/mutations.js';
import { useCancellationPolicyDetail } from '../api/queries.js';
import {
  type CancellationPolicyDetail,
  type CancellationPolicyFormInput,
  cancellationPolicyFormInput,
} from '../api/schemas.js';
import { CancellationPolicyFields } from '../forms/cancellation-policy-fields.js';

/**
 * Creating and editing a cancellation policy.
 *
 * One component for both, because they are the same fields under the same
 * validation; only the defaults, the title and the mutation differ.
 */

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

const CREATE_DEFAULTS: CancellationPolicyFormInput = {
  propertyId: '',
  code: '',
  name: '',
  description: '',
  contentHtml: '',
  status: POLICY_STATUS.ACTIVE,
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(policy: CancellationPolicyDetail): CancellationPolicyFormInput {
  return {
    propertyId: policy.propertyId,
    code: policy.code,
    name: policy.name,
    description: policy.description ?? '',
    contentHtml: policy.contentHtml ?? '',
    status: policy.status,
  };
}

export function CancellationPolicyFormPage(): React.JSX.Element {
  const policyId = useParams().policyId;
  const isEdit = policyId !== undefined;

  return (
    <PermissionGuard
      permission={
        isEdit ? PERMISSIONS.CANCELLATION_POLICY_UPDATE : PERMISSIONS.CANCELLATION_POLICY_CREATE
      }
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditCancellationPolicy policyId={policyId} /> : <CreateCancellationPolicy />}
    </PermissionGuard>
  );
}

function CreateCancellationPolicy(): React.JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const create = useCreateCancellationPolicy();

  const form = useAppForm<CancellationPolicyFormInput>({
    schema: cancellationPolicyFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Cancellation policy created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate({ pathname: `/cancellation-policies/${created.id}`, search });
    },
  });

  return (
    <FormPage
      title="New cancellation policy"
      description="The terms a guest agrees to when they book a rate that carries this policy."
      backTo="/cancellation-policies"
      backLabel="Cancellation policies"
      submitLabel="Create policy"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <CancellationPolicyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="create"
      />
    </FormPage>
  );
}

function EditCancellationPolicy({ policyId }: { readonly policyId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const update = useUpdateCancellationPolicy();
  const query = useCancellationPolicyDetail(policyId);

  if (query.isPending) return <LoadingState label="Loading cancellation policy" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditCancellationPolicyForm
      policy={query.data}
      onSaved={() => {
        void navigate({ pathname: `/cancellation-policies/${policyId}`, search });
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ policyId, version: query.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditCancellationPolicyForm({
  policy,
  save,
  onSaved,
}: {
  readonly policy: CancellationPolicyDetail;
  readonly save: (values: CancellationPolicyFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<CancellationPolicyFormInput>({
    schema: cancellationPolicyFormInput,
    defaultValues: defaultsFrom(policy),
    successMessage: 'Cancellation policy updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${policy.name}`}
      description="Changes take effect immediately for every rate plan that uses this policy."
      backTo="/cancellation-policies"
      backLabel="Cancellation policies"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <CancellationPolicyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="edit"
      />
    </FormPage>
  );
}
